// Modified version of this project:
// https://github.com/rednez/angular-user-idle/tree/develop/projects/angular-user-idle/src/lib

import {Injectable, Input, NgZone, OnDestroy} from '@angular/core';
import {  from,  fromEvent,  interval,  merge,  Observable,  of,  Subject,  Subscription,  timer,} from 'rxjs';
import {  bufferTime,  distinctUntilChanged, filter,  finalize,  map,  switchMap,  takeUntil,  tap,} from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
/*
  General Description:
  - For basic use, call startWatchingForUserEvents(), and subscribe to onTimeout().
  - To prompt after the user is idle, subscribe to onTimerStart().
  - To do something on a regular interval, subscribe to ping$.
  - To do something every time idle status changes, subscribe to onIdleStatusChanged.

  Behavior:
  - Call startWatchingForUserEvents()
    * An observable is started that looks for specific window events every second.
    * These events can be overriden by using setCustomActivityEvents().
  - If the user is idle for the time specified in idleMillisec, a final timeout countdown will start.
    * Subscribe to onTimerStart() to send the user a call to action to save their session.  
    * Any events at this point will reset the timer countdown.
  - If the final countdown time specified in timeoutSeconds elapses the user is timed out.
    * Subscribe to onTimeout() to take some action. Probably navigate the user to logoff or other session action.
  - Other notes:
    * There is a ping observable available that always fires on the pingMillisec interval if the user is not timed out.
*/

export class HhsgUserIdleService implements OnDestroy {
  /*
    Final timer countdown length in seconds before Timeout.
    Default 1 minute
  */
  @Input() timeoutSeconds = 1 * 60;

  /*
    Time before the user is considered idle and the final inactivity timer starts.
    Note: If a user performs an action after the timer has started, then the timer will reset.
    Can be used to warn a user of inactivity by subscribing to onTimerStart()
    Example:
    this._userIdle.onTimerStart().subscribe(count => {
      console.log('User-idle.service: User is idle for this many seconds: ' + count + '. Do something.');
    }
    Default 19 minutes
  */
  @Input() idleMillisec = 19 * 60 * 1000;

  /*
    Idle buffer wait time milliseconds to collect user action
    Default 1 second
  */
  @Input() idleSensitivityMillisec = 1000;

  /*
    Ping value in milliseconds.
    Can be used to do something on an interval.
    Example:
    this._userIdle.ping$.subscribe(x => {
    console.log("User-idle.service: check notifications.");
    })
    Default 2 minutes
  */
  @Input() pingMillisec = 120 * 1000;

  /*
   Enable print debug statements:
  */
  @Input() logEventsToConsole = false;


  /*
     Events that can interrupts user's inactivity timer.
  */
  protected activityEvents$!: Observable<any>;
  protected idleSubscription!: Subscription;
  protected timerStart$ = new Subject<boolean>();
  protected idleDetected$ = new Subject<boolean>();
  protected timeout$ = new Subject<boolean>();
  protected idle$!: Observable<any>;
  protected timer$!: Observable<any>;
  ping$!: Observable<any>;

  /*
    The Inactivity Timer is a countdown before calling the final timeout action.
    It can be reset by calling the ResetTimer() method in the right spot.
  */
  protected isInactivityTimer = false;
  protected isIdleDetected = false;
  protected isTimeout = false;

  constructor(private _ngZone: NgZone) {  }
  
  ngOnDestroy(): void {
    this.timerStart$.unsubscribe();
    this.idleDetected$.unsubscribe();
    this.idleDetected$.unsubscribe();
    this.idleSubscription.unsubscribe();
  }

  private logEventMessage(message: string) {
    if(this.logEventsToConsole) {
      console.log(`User-idle.service: ${message}`);
    }
  }

  /*
    Start watching for user idle and setup timer and ping.
  */
  startWatchingForUserEvents() {    
    if (!this.activityEvents$) {
      this.activityEvents$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'resize'),
        fromEvent(document, 'keydown')
      );
    }

    this.idle$ = from(this.activityEvents$);

    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    // If any of user events is not active for idle-seconds when start timer.
    // Note on how this works: 
    // We use a pipe function, so each comma seperated statment will be executed and passed to the next statement.
    // The order is as follows, 
    // 1. BufferTime  
    // 2. Filter - if not idle 
    // 3. Set user must be Idle 
    // 4. Start the final clock. 
    // Another note: The final clock is ONLY stopped by it's own observable checking isIdleDetected internally.
    this.idleSubscription = this.idle$
      .pipe(
        bufferTime(this.idleSensitivityMillisec),
        filter(
          (userEvents) =>
            // Note: Do NOT guard this call with !isTimerStarted or the final countdown cannot be stopped because isIdleDetected will never be set to false.
            // If we have an event from ANY of activityEvents observable above we need to bail out to the Complete.
            !userEvents.length && !this.isIdleDetected 
        ),
        tap(() => {
          // Tap can check the observable without altering state.
          // No events recorded in activityEvents$, so set idle.
          this.logEventMessage("isIdleDetected TRUE");
          this.isIdleDetected = true;
          this.idleDetected$.next(true);
        }),
        switchMap(() =>
          this._ngZone.runOutsideAngular(() =>
            interval(1000).pipe(
              takeUntil(
                merge(
                  this.activityEvents$,
                  // Start the final countdown to timeout:
                  timer(this.idleMillisec).pipe(
                    tap(() => {
                      if(!this.isInactivityTimer) {
                        this.logEventMessage("isInactivityTimer Started TRUE");
                        this.isInactivityTimer = true;
                        this.timerStart$.next(true);
                      }
                    })
                  )
                )
              ),
              finalize(() => {
                  this.logEventMessage("isIdleDetected FALSE");
                  this.isIdleDetected = false;
                  this.idleDetected$.next(false);
              })
            )
          )
        )
      )
      .subscribe();

    this.setupTimer(this.timeoutSeconds);
    this.setupPing(this.pingMillisec);
  }

  stopWatching() {
    this.stopTimer();
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  stopTimer() {
    this.isInactivityTimer = false;
    this.timerStart$.next(false);
  }

  resetTimer() {
    
    this.logEventMessage("RESET TIMER");
    this.stopTimer();
    this.isTimeout = false;
  }

  /*
    Return observable for the count down before final logoff.
    This should not be confused with the 1 second ping to determine if the user is idle.
  */
  onTimerStart(): Observable<number> {
    return this.timerStart$.pipe(
      distinctUntilChanged(),
      switchMap((start) => (start ? this.timer$ : of(null)))
    );
  }

  /*
    Return observable for idle status changed
  */
  onIdleStatusChanged(): Observable<boolean> {
    return this.idleDetected$.asObservable();
  }

  /*
    Return observable for timeout is fired.
    Subscribe and navigate to logout.
  */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter((timeout) => !!timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  /*
   Set custom activity events
  
   @param customEvents Example: merge(
     fromEvent(window, 'mousemove'),
     fromEvent(window, 'resize'),
     fromEvent(document, 'keydown'),
     fromEvent(document, 'touchstart'),
     fromEvent(document, 'touchend')
   )
  */
  setCustomActivityEvents(customEvents: Observable<any>) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set custom activity events');
      return;
    }

    this.activityEvents$ = customEvents;
  }

  /*
    Setup timer.
   
    Counts every seconds and return n+1 and fire timeout for last count.
    @param timeout Timeout in seconds.
  */
  protected setupTimer(timeout: number) {
    this._ngZone.runOutsideAngular(() => {
      this.timer$ = of(() => new Date()).pipe(
        map((fn) => fn()),
        switchMap((startDate) =>
          interval(1000).pipe(
            map(() =>
              Math.round((new Date().valueOf() - startDate.valueOf()) / 1000)
            ), //   convert elapsed count to seconds
            tap((elapsed) => {
              // Trigger the timeout action once we exceed the timeout period:
              if (elapsed >= timeout) {
                this.timeout$.next(true);
              }

              // If the user stops being Idle, RESET the timer to avert final timeout!
              if(this.isIdleDetected === false)
              {
                this.logEventMessage("isInactivityTimer false, calling ResetTimer()");
                this.resetTimer();
              }
            })
          )
        )
      );
    });
  }

  /*
    Setup call to action ping for inactivity.
   
    Pings every ping-seconds only if the user is not timeout.
    @param pingMillisec
  */
  protected setupPing(pingMillisec: number) {
    this.ping$ = interval(pingMillisec).pipe(filter(() => !this.isTimeout));
  }
}
